/**
 * CAIO Dashboard — extracted panels.
 * Each panel is a small, focused component that reads from `useDashboardData()`
 * so the SAME panel works in Demo Mode (rich) and (if mounted) Customer Mode.
 *
 * Keeping panels small improves: readability, testability, code review speed,
 * and React rendering granularity.
 */
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowRight,
  ChevronRight,
  CircleDot,
  Eye,
  Sparkles,
  Flame,
} from "lucide-react";
import { useDashboardData } from "@/contexts/DashboardDataContext";
import { Flywheel } from "@/components/brand/Flywheel";
import { cn } from "@/lib/utils";
import {
  fmtAud,
  PanelCard,
  LinearProgress,
  SeverityChip,
  Sparkline,
} from "./DashboardPrimitives";
import { optimisationNudge } from "@/lib/costRollups";
import { TrendChip } from "@/components/data/TrendChip";

// ───────────────────────────────────────────────────────────────────────────
// Period selector
// ───────────────────────────────────────────────────────────────────────────
const PERIOD_OPTIONS = ["7d", "30d", "QTD", "YTD"];

export function PeriodSelector() {
  const [period, setPeriod] = useState("30d");
  return (
    <div
      className="inline-flex items-center rounded-md border border-border-subtle bg-raised p-0.5"
      data-testid="caio-period-selector"
    >
      {PERIOD_OPTIONS.map((opt) => (
        <button
          key={opt}
          type="button"
          onClick={() => setPeriod(opt)}
          className={cn(
            "px-3 h-7 text-meta font-semibold rounded transition-colors",
            period === opt
              ? "bg-tralenex-600 text-white"
              : "text-ink-secondary hover:text-ink-primary",
          )}
          data-testid={`caio-period-${opt.toLowerCase()}`}
        >
          {opt}
        </button>
      ))}
    </div>
  );
}

// ───────────────────────────────────────────────────────────────────────────
// Stage dots — used by approvals queue
// ───────────────────────────────────────────────────────────────────────────
function StageDots({ stage }) {
  return (
    <div className="flex items-center gap-1 mt-1 shrink-0">
      {[1, 2, 3, 4, 5].map((s) => (
        <span
          key={s}
          className={cn(
            "h-1.5 w-1.5 rounded-full",
            s < stage
              ? "bg-tralenex-600"
              : s === stage
                ? "bg-gold-600"
                : "bg-border-strong",
          )}
        />
      ))}
    </div>
  );
}

// ───────────────────────────────────────────────────────────────────────────
// Flywheel status — 4 stages (Identify · Deploy · Govern · Measure)
// ───────────────────────────────────────────────────────────────────────────
const DOT_TONE = {
  opp: "bg-opportunity",
  tralenex: "bg-tralenex-600",
  low: "bg-risk-low",
  gold: "bg-gold-600",
};

export function FlywheelPanel() {
  const { kpis, flywheel = {} } = useDashboardData();

  const stages = [
    { key: "Identify", value: flywheel.opportunitiesTotal || 0, label: "opportunities", tone: "opp" },
    { key: "Deploy", value: flywheel.requestsInFlight || 0, label: "in workflow", tone: "tralenex" },
    { key: "Govern", value: flywheel.agentsLive || 0, label: "live agents", tone: "low" },
    {
      key: "Measure",
      value: `${(kpis.roiRatio || 0).toFixed(1)}×`,
      label: "ROI tracked",
      tone: "gold",
    },
  ];

  return (
    <div
      className="surface-card shadow-e1 relative overflow-hidden"
      data-testid="caio-flywheel-panel"
    >
      <div className="absolute -right-12 -bottom-12 pointer-events-none">
        <Flywheel size={220} opacity={0.06} />
      </div>
      <div className="px-5 pt-4 pb-3 border-b border-border-subtle flex items-center gap-2">
        <Sparkles size={14} className="text-gold-600" />
        <p className="text-h3 text-ink-primary">Flywheel status</p>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 divide-y md:divide-y-0 md:divide-x divide-border-subtle">
        {stages.map((s) => (
          <div key={s.key} className="px-5 py-4 relative">
            <div className="flex items-center gap-2">
              <span className={cn("h-1.5 w-1.5 rounded-full", DOT_TONE[s.tone])} />
              <p className="eyebrow">{s.key}</p>
            </div>
            <p className="mt-2 text-display-md text-ink-primary tabular-nums leading-none">
              {s.value}
            </p>
            <p className="mt-1 text-meta text-ink-secondary">{s.label}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

// ───────────────────────────────────────────────────────────────────────────
// Top opportunities by est. annual ROI
// ───────────────────────────────────────────────────────────────────────────
export function OpportunitiesPanel() {
  const navigate = useNavigate();
  const { opportunities } = useDashboardData();
  const top = [...opportunities]
    .sort((a, b) => b.estAnnualRoiAud - a.estAnnualRoiAud)
    .slice(0, 5);

  return (
    <PanelCard
      title="Top opportunities by est. annual ROI"
      testId="caio-opportunities-panel"
      action={
        <button
          type="button"
          onClick={() => navigate("/identify/opportunities")}
          className="text-meta font-semibold text-tralenex-700 hover:text-tralenex-900 inline-flex items-center gap-1"
          data-testid="caio-open-radar"
        >
          Open radar <ArrowRight size={12} />
        </button>
      }
    >
      <ul className="divide-y divide-border-subtle -my-2">
        {top.map((o) => (
          <li
            key={o.id}
            className="py-2.5 flex items-center gap-3"
            data-testid={`caio-opp-${o.id}`}
          >
            <span className="text-meta font-mono text-ink-disabled tabular-nums w-10 shrink-0">
              #{o.rank.toString().padStart(2, "0")}
            </span>
            <div className="flex-1 min-w-0">
              <p className="text-sm text-ink-primary truncate font-medium">{o.title}</p>
              <p className="text-meta text-ink-secondary truncate">
                {o.department} · {o.owner} · effort {o.effort} · risk {o.risk}
              </p>
            </div>
            <Sparkline data={o.sparkline} />
            <span className="text-sm font-semibold text-ink-primary tabular-nums w-[88px] text-right shrink-0">
              {fmtAud(o.estAnnualRoiAud)}
            </span>
            <SeverityChip severity={o.risk} />
          </li>
        ))}
      </ul>
    </PanelCard>
  );
}

// ───────────────────────────────────────────────────────────────────────────
// ROI by department
// ───────────────────────────────────────────────────────────────────────────
function DeptRow({ d }) {
  return (
    <div
      className="flex items-center gap-3"
      data-testid={`caio-dept-${d.department.replace(/\s+/g, "-").toLowerCase()}`}
    >
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between">
          <p className="text-sm text-ink-primary truncate font-medium">{d.department}</p>
          <span className="text-meta text-ink-secondary tabular-nums shrink-0">
            {d.roi.toFixed(1)}×
          </span>
        </div>
        <div className="mt-1">
          <LinearProgress
            value={Math.min(100, (d.roi / 5) * 100)}
            tone={d.roi >= 3 ? "low" : d.roi >= 2 ? "tralenex" : "risk"}
          />
        </div>
        <p className="mt-1 text-meta text-ink-disabled">
          {d.agents} agents · {fmtAud(d.spendAud)} spend · {fmtAud(d.valueAud)} value
        </p>
      </div>
    </div>
  );
}

export function DepartmentsPanel() {
  const { departmentRollups } = useDashboardData();
  return (
    <PanelCard
      title="ROI by department"
      testId="caio-departments-panel"
      footer={`${departmentRollups.length} departments · governed agents only`}
    >
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3">
        {departmentRollups.slice(0, 8).map((d) => (
          <DeptRow key={d.department} d={d} />
        ))}
      </div>
    </PanelCard>
  );
}

// ───────────────────────────────────────────────────────────────────────────
// Top burners (this month) — surface the heaviest-spending governed agents
// so a CFO/CIO can spot concentration at a glance. Click a row to open the
// Agent Detail. Nudge tag is rendered when an optimisation is plausible.
// ───────────────────────────────────────────────────────────────────────────
function TopBurnerRow({ a, tenantName, onOpen }) {
  const nudge = optimisationNudge(a);
  return (
    <button
      type="button"
      onClick={() => onOpen(a)}
      className="w-full text-left grid grid-cols-12 gap-3 items-center py-2.5 px-2 -mx-2 rounded-md hover:bg-inset/40 transition-colors"
      data-testid={`caio-burner-row-${a.id}`}
    >
      <div className="col-span-5 min-w-0">
        <p className="text-sm font-semibold text-ink-primary truncate">{a.name}</p>
        <p className="text-meta text-ink-secondary truncate">
          <span className="font-mono tabular-nums">{a.id}</span> · {a.businessUnit || a.department}
          {tenantName && <span className="text-ink-disabled"> · {tenantName}</span>}
        </p>
        {nudge && (
          <span
            className="mt-1 inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-semibold tracking-[0.04em] bg-gold-300/15 text-gold-700"
            data-testid={`caio-burner-nudge-${a.id}`}
          >
            <Sparkles size={9} />
            Switch {nudge.fromModel} → {nudge.toModel} · save ~{fmtAud(nudge.savePerMo)}/mo
          </span>
        )}
      </div>
      <div className="col-span-2 text-right">
        <div className="inline-flex flex-col items-end gap-0.5">
          <p className="text-sm font-semibold text-ink-primary tabular-nums">{fmtAud(a.monthlyCostAud)}</p>
          <TrendChip id={a.id} size="xs" />
        </div>
      </div>
      <div className="col-span-2 text-right">
        <p className="text-sm text-risk-low tabular-nums font-semibold">{fmtAud(a.monthlyValueAud)}</p>
      </div>
      <div className="col-span-2 text-right">
        <p className="text-sm font-semibold text-tralenex-700 tabular-nums">{(a.roiMultiple || 0).toFixed(1)}×</p>
      </div>
      <div className="col-span-1 text-right text-ink-disabled">
        <ChevronRight size={14} />
      </div>
    </button>
  );
}

export function TopBurnersPanel() {
  const { topBurners = [], tenant } = useDashboardData();
  const navigate = useNavigate();
  const open = (a) => navigate(`/govern/agents/${a.id}`);

  if (!topBurners.length) return null;

  const total = topBurners.reduce((s, a) => s + (a.monthlyCostAud || 0), 0);

  return (
    <PanelCard
      title={
        <span className="inline-flex items-center gap-2">
          <Flame size={14} className="text-gold-600" />
          Top 5 burners this month
        </span>
      }
      testId="caio-top-burners-panel"
      footer={`${topBurners.length} agents · ${fmtAud(total)} combined monthly burn · click to open`}
      action={
        <button
          type="button"
          onClick={() => navigate("/measure/cost")}
          className="inline-flex items-center gap-1 text-meta font-semibold text-tralenex-700 hover:text-tralenex-900"
          data-testid="caio-top-burners-cta"
        >
          See cost detail <ArrowRight size={12} />
        </button>
      }
    >
      <div className="divide-y divide-border-subtle/60">
        <div
          className="grid grid-cols-12 gap-3 items-center px-2 -mx-2 pb-2 text-[10px] font-bold tracking-[0.08em] uppercase text-ink-secondary"
          data-testid="caio-top-burners-header"
        >
          <div className="col-span-5">Agent</div>
          <div className="col-span-2 text-right">Burn / mo</div>
          <div className="col-span-2 text-right">Value / mo</div>
          <div className="col-span-2 text-right">ROI</div>
          <div className="col-span-1" />
        </div>
        {topBurners.map((a) => (
          <TopBurnerRow key={a.id} a={a} tenantName={tenant?.name} onOpen={open} />
        ))}
      </div>
    </PanelCard>
  );
}

// ───────────────────────────────────────────────────────────────────────────
// My approvals queue
// ───────────────────────────────────────────────────────────────────────────
export function ApprovalsPanel() {
  const navigate = useNavigate();
  const { requests } = useDashboardData();
  const pending = requests.filter((r) => r.stage < 5).slice(0, 4);

  return (
    <PanelCard
      title="My approvals queue"
      testId="caio-approvals-panel"
      action={
        <button
          type="button"
          onClick={() => navigate("/deploy/requests")}
          className="text-meta font-semibold text-tralenex-700 hover:text-tralenex-900 inline-flex items-center gap-1"
          data-testid="caio-open-approvals"
        >
          View all <ArrowRight size={12} />
        </button>
      }
    >
      <ul className="space-y-3 -my-1">
        {pending.map((r) => (
          <li key={r.id} className="flex items-start gap-3" data-testid={`caio-appr-${r.id}`}>
            <StageDots stage={r.stage} />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-ink-primary truncate">{r.agentName}</p>
              <p className="text-meta text-ink-secondary truncate">
                {r.id} · {r.department} · risk {r.risk}
              </p>
            </div>
            <span className="text-meta text-ink-disabled tabular-nums shrink-0">{r.slaDue}</span>
          </li>
        ))}
        {pending.length === 0 && (
          <li className="text-meta text-ink-secondary">All clear.</li>
        )}
      </ul>
    </PanelCard>
  );
}

// ───────────────────────────────────────────────────────────────────────────
// Shadow AI watch
// ───────────────────────────────────────────────────────────────────────────
export function ShadowAiPanel() {
  const { agents } = useDashboardData();
  const shadows = agents.filter((a) => a.status === "ungoverned");

  return (
    <PanelCard
      title="Shadow AI watch"
      testId="caio-shadow-panel"
      action={
        <span
          className={cn(
            "inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-bold tracking-[0.08em] uppercase",
            shadows.length > 0
              ? "bg-shadow-red/10 text-shadow-red pulse-ungoverned"
              : "bg-risk-low/10 text-risk-low",
          )}
          data-testid="caio-shadow-count"
        >
          {shadows.length} ungoverned
        </span>
      }
    >
      <ul className="space-y-2.5 -my-1">
        {shadows.slice(0, 3).map((a) => (
          <li key={a.id} className="flex items-start gap-2">
            <Eye size={13} className="text-shadow-red mt-0.5 shrink-0" />
            <div className="min-w-0">
              <p className="text-sm text-ink-primary font-medium truncate">{a.name}</p>
              <p className="text-meta text-ink-secondary truncate">
                {a.department} · {a.purpose}
              </p>
            </div>
          </li>
        ))}
        {shadows.length === 0 && (
          <li className="text-meta text-ink-secondary">No shadow AI detected.</li>
        )}
      </ul>
    </PanelCard>
  );
}

// ───────────────────────────────────────────────────────────────────────────
// Compliance posture
// ───────────────────────────────────────────────────────────────────────────
const tonePerCoverage = (coverage) =>
  coverage >= 90 ? "low" : coverage >= 75 ? "tralenex" : "risk";

export function CompliancePanel() {
  const { frameworks } = useDashboardData();
  return (
    <PanelCard title="Compliance posture" testId="caio-compliance-panel">
      <ul className="space-y-2.5 -my-1">
        {frameworks.slice(0, 5).map((f) => (
          <li key={f.id}>
            <div className="flex items-center justify-between mb-1">
              <p className="text-sm text-ink-primary truncate">{f.name}</p>
              <span className="text-meta text-ink-secondary tabular-nums">{f.coverage}%</span>
            </div>
            <LinearProgress value={f.coverage} tone={tonePerCoverage(f.coverage)} />
          </li>
        ))}
      </ul>
    </PanelCard>
  );
}

// ───────────────────────────────────────────────────────────────────────────
// Top open incidents
// ───────────────────────────────────────────────────────────────────────────
export function IncidentsPanel() {
  const { incidents } = useDashboardData();
  const open = incidents.filter((i) => i.status !== "closed").slice(0, 3);
  return (
    <PanelCard title="Top incidents (open)" testId="caio-incidents-panel">
      <ul className="space-y-2.5 -my-1">
        {open.map((i) => (
          <li key={i.id} className="flex items-start gap-2.5">
            <SeverityChip severity={i.severity} />
            <div className="min-w-0 flex-1">
              <p className="text-sm text-ink-primary truncate">{i.title}</p>
              <p className="text-meta text-ink-secondary truncate">
                {i.id} · owner {i.owner} · {i.days}d open
              </p>
            </div>
          </li>
        ))}
        {open.length === 0 && (
          <li className="text-meta text-ink-secondary">No open incidents.</li>
        )}
      </ul>
    </PanelCard>
  );
}

// ───────────────────────────────────────────────────────────────────────────
// Vendor health
// ───────────────────────────────────────────────────────────────────────────
const vendorStatusClass = (status) =>
  status === "healthy"
    ? "text-risk-low"
    : status === "degraded"
      ? "text-risk-med"
      : "text-risk-high";

export function VendorsPanel() {
  const { vendorRollups } = useDashboardData();
  return (
    <PanelCard title="Vendor health" testId="caio-vendors-panel">
      <ul className="space-y-2 -my-1">
        {vendorRollups.slice(0, 6).map((v) => (
          <li key={v.id} className="flex items-center gap-2.5 text-sm">
            <CircleDot size={10} className={vendorStatusClass(v.status)} />
            <span className="flex-1 min-w-0 truncate text-ink-primary">{v.name}</span>
            <span className="text-meta text-ink-secondary tabular-nums shrink-0">
              {v.agents} agents · {v.latencyMs}ms
            </span>
          </li>
        ))}
      </ul>
    </PanelCard>
  );
}

// ───────────────────────────────────────────────────────────────────────────
// Live audit ticker — uses stable composite key (NOT array index)
// ───────────────────────────────────────────────────────────────────────────
const auditKey = (entry) => `${entry.ts}|${entry.actor}|${entry.action}|${entry.target}`;

export function AuditTicker() {
  const navigate = useNavigate();
  const { auditEntries } = useDashboardData();

  return (
    <div className="surface-card shadow-e1" data-testid="caio-audit-ticker">
      <div className="px-5 py-3 border-b border-border-subtle flex items-center justify-between">
        <p className="eyebrow flex items-center gap-2">
          Live audit · last 6 events
          <span className="inline-flex h-1.5 w-1.5 rounded-full bg-risk-low pulse-ungoverned" />
        </p>
        <button
          type="button"
          onClick={() => navigate("/settings/audit")}
          className="text-meta font-semibold text-tralenex-700 hover:text-tralenex-900 inline-flex items-center gap-1"
          data-testid="caio-open-audit"
        >
          Open full log <ChevronRight size={12} />
        </button>
      </div>
      <ul className="divide-y divide-border-subtle">
        {auditEntries.slice(0, 6).map((e) => (
          <li
            key={auditKey(e)}
            className="px-5 py-2 flex items-center gap-3 text-meta hover:bg-inset/40 transition-colors"
          >
            <span className="font-mono text-ink-disabled tabular-nums w-[180px] shrink-0">
              {e.ts}
            </span>
            <span className="font-mono text-tralenex-700 w-[200px] shrink-0 truncate">
              {e.action}
            </span>
            <span className="text-ink-primary truncate flex-1">{e.target}</span>
            <span className="text-ink-secondary shrink-0">{e.actor}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}
